import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Trash2, Loader2, Flame, Pause, Play, Check, X, Calendar, AlarmClock, MessageSquare, Pencil, Share2, LayoutGrid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEscapeToHome } from '@/hooks/useKeyboard';
import ShareDialog from '@/components/ShareDialog';

const FREQ_OPTS = [{value:'daily',label:'Daily'},{value:'weekly',label:'Weekly'},{value:'custom',label:'Custom'}];
const DIFF_OPTS = [{value:'easy',label:'Easy'},{value:'medium',label:'Medium'},{value:'hard',label:'Hard'}];
const COLOR_PRESETS = ['#ef4444','#f97316','#f59e0b','#22c55e','#3b82f6','#a855f7','#ec4899','#14b8a6'];

export default function HabitsPage() {
  const { activeProject, projects } = useApp();
  useEscapeToHome();
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedHabit, setSelectedHabit] = useState(null);
  const [form, setForm] = useState({ title: '', project_id: '', description: '', frequency: 'daily', difficulty: 'medium', color: '#3b82f6', tags: '', start_date: '' });
  const [shareHabit, setShareHabit] = useState(null);
  const [viewMode, setViewMode] = useState('habit'); // 'habit' | 'calendar'
  const [calMonthOffset, setCalMonthOffset] = useState(0);

  const fetchHabits = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/habits', { params: { project_id: activeProject } }); setHabits(data.habits); } catch {}
    setLoading(false);
  }, [activeProject]);

  useEffect(() => { fetchHabits(); }, [fetchHabits]);

  // 'N' to create new, 'V' to toggle view
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'n' && !isTyping) { e.preventDefault(); openCreate(); }
      else if (e.key === 'v' && !isTyping) { e.preventDefault(); setViewMode(m => m === 'habit' ? 'calendar' : 'habit'); }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  });

  useEffect(() => {
    if (selectedHabit) {
      const upd = habits.find(h => h.id === selectedHabit.id);
      if (upd) setSelectedHabit(upd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habits]);

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p.name; });

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', project_id: activeProject === 'all' ? '' : activeProject, description: '', frequency: 'daily', difficulty: 'medium', color: '#3b82f6', tags: '', start_date: new Date().toISOString().slice(0, 10) });
    setShowForm(true);
  };

  const focusFormField = (idx) => { setTimeout(() => { const el = document.querySelector(`[data-form-idx="${idx}"]`); if (el) { el.focus(); if (el.getAttribute('role') === 'combobox') el.click(); } }, 60); };
  const handleFormKeyDown = (e, idx, total) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); if (idx >= total - 1) save(); else focusFormField(idx + 1); } };

  const save = async () => {
    if (!form.title || !form.project_id) { toast.error('Title and workspace required'); return; }
    const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], start_date: form.start_date || null };
    try {
      if (editing) { await api.put(`/habits/${editing.id}`, payload); toast.success('Habit updated'); }
      else { await api.post('/habits', payload); toast.success('Habit created'); }
      setShowForm(false); fetchHabits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleComplete = async (habit, date) => {
    const isCompleted = (habit.completions || []).some(c => c.date === date);
    try {
      if (isCompleted) { await api.post(`/habits/${habit.id}/uncomplete`, { date }); }
      else { await api.post(`/habits/${habit.id}/complete`, { date }); }
      fetchHabits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const togglePause = async (habit) => {
    try { await api.put(`/habits/${habit.id}`, { is_paused: !habit.is_paused }); fetchHabits(); toast.success(habit.is_paused ? 'Habit resumed' : 'Habit paused'); } catch {}
  };

  const deleteHabit = async (id) => {
    if (!window.confirm('Move this habit to trash?')) return;
    try { await api.delete(`/habits/${id}`); toast('Habit moved to trash'); fetchHabits(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Calendar: last 7 days
  const today = new Date();
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today); d.setDate(d.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });
  const dayLabels = last7.map(d => new Date(d).toLocaleDateString('en', { weekday: 'short' }));
  const todayStr = today.toISOString().slice(0, 10);

  if (selectedHabit) {
    return <HabitDetail habit={selectedHabit} projects={projects} onClose={() => setSelectedHabit(null)}
      onRefresh={fetchHabits} onToggleComplete={toggleComplete} last7={last7} todayStr={todayStr} dayLabels={dayLabels} />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="habits-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Habits</h2>
            <p className="text-sm text-zinc-500 mt-1">{habits.length} habit{habits.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
              <button onClick={() => setViewMode('habit')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'habit' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="habits-view-habit">
                <List className="h-3.5 w-3.5" strokeWidth={1.5} /> Habit</button>
              <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'calendar' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="habits-view-calendar">
                <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} /> Calendar</button>
            </div>
            <Button onClick={openCreate} className="rounded-xl" data-testid="create-habit-btn"><Plus className="h-4 w-4 mr-1" /> New Habit</Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:px-8">
        <div className="w-full">
        {viewMode === 'habit' ? (
        <>
          {/* Week header */}
          <div className="grid grid-cols-[1fr_repeat(7,48px)] md:grid-cols-[1fr_repeat(7,56px)] gap-1 mb-2 items-end">
            <div></div>
            {dayLabels.map((d, i) => (
              <div key={i} className={`text-center text-[10px] font-medium py-1 ${last7[i] === todayStr ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400'}`}>
                {d}<br /><span className="text-[9px]">{last7[i].slice(8)}</span>
              </div>
            ))}
          </div>

          {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
          : habits.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-zinc-500 text-sm mb-4">No habits yet. Start building consistency.</p>
              <Button onClick={openCreate} variant="outline"><Plus className="h-4 w-4 mr-1" /> New Habit</Button>
            </div>
          ) : (
            <div className="space-y-1">
              {habits.map(habit => {
                const completedDates = new Set((habit.completions || []).map(c => c.date));
                return (
                  <div key={habit.id} className={`grid grid-cols-[1fr_repeat(7,48px)] md:grid-cols-[1fr_repeat(7,56px)] gap-1 items-center py-2 border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors group ${habit.is_paused ? 'opacity-50' : ''}`}
                    data-testid={`habit-row-${habit.id}`}>
                    {/* Habit info */}
                    <div className="flex items-center gap-3 min-w-0 cursor-pointer pr-2" onClick={() => setSelectedHabit(habit)}>
                      <div className="w-3 h-8 rounded-full shrink-0" style={{ backgroundColor: habit.color }} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{habit.title}</span>
                          {habit.is_paused && <Badge variant="outline" className="text-[9px] px-1">Paused</Badge>}
                          {habit.is_shared_with_me && <Badge variant="secondary" className="text-[9px] px-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" data-testid={`habit-shared-${habit.id}`}>Shared</Badge>}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                          <span>{projectMap[habit.project_id]}</span>
                          <Flame className="h-3 w-3 text-orange-500" />
                          <span>{habit.current_streak}d streak</span>
                        </div>
                      </div>
                      {!habit.is_shared_with_me && <button onClick={(e) => { e.stopPropagation(); setShareHabit(habit); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded shrink-0" data-testid={`share-habit-${habit.id}`}><Share2 className="h-3.5 w-3.5 text-zinc-400" /></button>}
                      <button onClick={(e) => { e.stopPropagation(); deleteHabit(habit.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-950 rounded shrink-0" data-testid={`delete-habit-${habit.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                    </div>
                    {/* 7-day grid */}
                    {last7.map(date => {
                      const done = completedDates.has(date);
                      const isToday = date === todayStr;
                      return (
                        <button key={date} onClick={() => toggleComplete(habit, date)}
                          className={`w-full aspect-square rounded-lg flex items-center justify-center transition-all
                            ${done ? '' : isToday ? 'border-2 border-dashed border-zinc-300 dark:border-zinc-600' : 'bg-zinc-100 dark:bg-zinc-800'}
                            ${done ? 'hover:opacity-80' : 'hover:bg-zinc-200 dark:hover:bg-zinc-700'}`}
                          style={done ? { backgroundColor: habit.color } : {}}
                          data-testid={`habit-${habit.id}-${date}`}>
                          {done && <Check className="h-4 w-4 text-white" strokeWidth={2.5} />}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </>
        ) : (
          // CALENDAR VIEW — per-habit month grid
          (() => {
            const base = new Date();
            base.setDate(1);
            base.setMonth(base.getMonth() + calMonthOffset);
            const year = base.getFullYear();
            const month = base.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const monthLabel = base.toLocaleDateString('en', { month: 'long', year: 'numeric' });
            const pad = (n) => String(n).padStart(2, '0');
            const days = Array.from({ length: daysInMonth }, (_, i) => `${year}-${pad(month + 1)}-${pad(i + 1)}`);
            const monthHabits = habits;
            return (
              <div data-testid="habits-calendar-view">
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" size="sm" onClick={() => setCalMonthOffset(o => o - 1)} data-testid="cal-prev-month"><ChevronLeft className="h-4 w-4 mr-1" />Prev</Button>
                  <h3 className="text-lg font-medium">{monthLabel}</h3>
                  <Button variant="ghost" size="sm" onClick={() => setCalMonthOffset(o => o + 1)} data-testid="cal-next-month">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
                </div>
                {monthHabits.length === 0 ? (
                  <div className="py-20 text-center text-sm text-zinc-500">No habits to show</div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-fit">
                      {/* Header row: day numbers */}
                      <div className="flex items-center gap-1 mb-2 sticky top-0 z-10 bg-white dark:bg-zinc-950 pb-2">
                        <div className="w-56 shrink-0 text-[10px] font-medium text-zinc-400 uppercase tracking-wider px-2">Habit · Streak</div>
                        {days.map(d => (
                          <div key={d} className={`w-7 text-center text-[10px] ${d === todayStr ? 'text-zinc-900 dark:text-zinc-100 font-bold' : 'text-zinc-400'}`}>
                            {d.slice(8)}
                          </div>
                        ))}
                      </div>
                      {/* Per-habit row */}
                      {monthHabits.map(habit => {
                        const completed = new Set((habit.completions || []).map(c => c.date));
                        return (
                          <div key={habit.id} className={`flex items-center gap-1 mb-1 group hover:bg-zinc-50 dark:hover:bg-zinc-900 rounded-lg ${habit.is_paused ? 'opacity-50' : ''}`} data-testid={`cal-habit-row-${habit.id}`}>
                            <div className="w-56 shrink-0 flex items-center gap-2 px-2 py-1 cursor-pointer" onClick={() => setSelectedHabit(habit)}>
                              <div className="w-2 h-8 rounded-full shrink-0" style={{ backgroundColor: habit.color }} />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium truncate">{habit.title}</p>
                                <div className="flex items-center gap-1 text-[10px] text-zinc-400">
                                  <Flame className="h-3 w-3 text-orange-500" />
                                  <span>{habit.current_streak}d streak</span>
                                </div>
                              </div>
                            </div>
                            {days.map(d => {
                              const done = completed.has(d);
                              const isToday = d === todayStr;
                              const isFuture = new Date(d) > new Date(todayStr);
                              return (
                                <button key={d} onClick={() => !isFuture && toggleComplete(habit, d)} disabled={isFuture}
                                  className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${done ? '' : isToday ? 'border-2 border-dashed border-zinc-300 dark:border-zinc-600' : isFuture ? 'bg-zinc-50 dark:bg-zinc-900/50' : 'bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700'}`}
                                  style={done ? { backgroundColor: habit.color } : {}}
                                  data-testid={`cal-${habit.id}-${d}`}>
                                  {done && <Check className="h-3 w-3 text-white" strokeWidth={2.5} />}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}
        </div>
      </div>

      {/* Create Form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="habit-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Habit' : 'New Habit'}</DialogTitle><DialogDescription>Build a consistent routine</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="e.g., Morning Exercise" className="h-12" data-form-idx="0" onKeyDown={e => handleFormKeyDown(e, 0, 5)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Workspace *</Label>
                <SearchableSelect value={form.project_id} onValueChange={v => setForm(p => ({...p, project_id: v}))} options={projects.map(p => ({value:p.id,label:p.name}))} placeholder="Select" data-form-idx="1" onMoveToNext={() => focusFormField(2)} /></div>
              <div className="space-y-2"><Label>Frequency</Label>
                <SearchableSelect value={form.frequency} onValueChange={v => setForm(p => ({...p, frequency: v}))} options={FREQ_OPTS} placeholder="Frequency" data-form-idx="2" onMoveToNext={() => focusFormField(3)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Difficulty</Label>
                <SearchableSelect value={form.difficulty} onValueChange={v => setForm(p => ({...p, difficulty: v}))} options={DIFF_OPTS} placeholder="Difficulty" data-form-idx="3" onMoveToNext={() => focusFormField(4)} /></div>
              <div className="space-y-2"><Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} data-form-idx="4" tabIndex={0} onKeyDown={e => handleFormKeyDown(e, 4, 5)} /></div>
            </div>
            <div className="space-y-2"><Label>Color</Label>
              <div className="flex gap-2">{COLOR_PRESETS.map(c => (
                <button key={c} onClick={() => setForm(p => ({...p, color: c}))} className={`w-8 h-8 rounded-full border-2 ${form.color === c ? 'border-zinc-900 dark:border-white scale-110' : 'border-transparent'}`} style={{backgroundColor: c}} />
              ))}</div></div>
            <div className="space-y-2"><Label>Tags</Label><Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="tag1, tag2" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-xl">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {shareHabit && <ShareDialog open={!!shareHabit} onClose={() => setShareHabit(null)} recordType="habit" recordId={shareHabit.id} recordName={shareHabit.title} />}
    </div>
  );
}

// ─── Habit Detail (Full-Screen) ───
function HabitDetail({ habit, projects, onClose, onRefresh, onToggleComplete, last7, todayStr, dayLabels }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activityForm, setActivityForm] = useState({ description: '', next_followup: '' });
  const [shareOpen, setShareOpen] = useState(false);

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p.name; });

  useEffect(() => {
    if (habit) setForm({ title: habit.title, description: habit.description || '', frequency: habit.frequency, difficulty: habit.difficulty, color: habit.color, tags: (habit.tags || []).join(', ') });
  }, [habit]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      const openPopover = document.querySelector('[data-radix-popper-content-wrapper]');
      if (openPopover) return;
      if (editing) { setEditing(false); e.stopImmediatePropagation(); return; }
      onClose(); e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [editing, onClose]);

  const saveEdit = async () => {
    try {
      await api.put(`/habits/${habit.id}`, { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] });
      toast.success('Updated'); setEditing(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addActivity = async () => {
    if (!activityForm.description) { toast.error('Description required'); return; }
    try { await api.post(`/habits/${habit.id}/activity`, activityForm); toast.success('Activity added'); setActivityForm({ description: '', next_followup: '' }); onRefresh(); }
    catch { toast.error('Error'); }
  };

  const completedDates = new Set((habit.completions || []).map(c => c.date));
  const activityLog = (habit.activity_log || []).slice().reverse();
  // Last 30 days calendar
  const cal30 = Array.from({ length: 30 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - (29 - i)); return d.toISOString().slice(0, 10); });

  return (
    <div className="fixed inset-0 z-[60] bg-white dark:bg-zinc-950 overflow-hidden flex flex-col" data-testid="task-detail">
      <div className="flex items-center justify-between h-12 px-4 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 truncate">{projectMap[habit.project_id]}</button>
          <span className="text-zinc-300">/</span>
          <span className="text-zinc-900 dark:text-zinc-100 font-medium truncate">{habit.title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!editing && !habit.is_shared_with_me && <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} data-testid="habit-detail-share-btn"><Share2 className="h-3.5 w-3.5 mr-1" /> Share</Button>}
          {habit.is_shared_with_me && <Badge variant="secondary" className="text-[10px] bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">Shared with you</Badge>}
          {!editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          {editing && <Button size="sm" onClick={saveEdit}><Check className="h-3.5 w-3.5 mr-1" /> Save</Button>}
          {editing && <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>}
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 md:p-6 md:border-r border-zinc-200 dark:border-zinc-800">
          {editing ? <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="text-2xl font-semibold h-auto py-2 mb-4" />
            : <h1 className="text-2xl font-semibold tracking-tight mb-2">{habit.title}</h1>}
          <p className="text-sm text-zinc-500 mb-6">{habit.description || 'No description'}</p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1"><Flame className="h-5 w-5 text-orange-500" /></div>
              <p className="text-2xl font-semibold">{habit.current_streak}</p><p className="text-[10px] text-zinc-400 uppercase">Current Streak</p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 text-center">
              <p className="text-2xl font-semibold">{habit.best_streak}</p><p className="text-[10px] text-zinc-400 uppercase">Best Streak</p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 text-center">
              <p className="text-2xl font-semibold">{(habit.completions || []).length}</p><p className="text-[10px] text-zinc-400 uppercase">Total Done</p>
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div><Label className="text-[11px] text-zinc-400 uppercase tracking-wider">Frequency</Label><p className="text-sm mt-1 capitalize">{habit.frequency}</p></div>
            <div><Label className="text-[11px] text-zinc-400 uppercase tracking-wider">Difficulty</Label><p className="text-sm mt-1 capitalize">{habit.difficulty}</p></div>
            <div><Label className="text-[11px] text-zinc-400 uppercase tracking-wider">Status</Label><p className="text-sm mt-1">{habit.is_paused ? 'Paused' : 'Active'}</p></div>
          </div>

          {/* 30-day Calendar */}
          <Label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2 block">Last 30 Days</Label>
          <div className="flex flex-wrap gap-1 mb-6">
            {cal30.map(date => {
              const done = completedDates.has(date);
              return (
                <button key={date} onClick={() => onToggleComplete(habit, date)}
                  className={`w-8 h-8 rounded-md text-[9px] flex items-center justify-center transition-all ${done ? 'text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 hover:bg-zinc-200'}`}
                  style={done ? { backgroundColor: habit.color } : {}}>
                  {done ? <Check className="h-3 w-3" strokeWidth={3} /> : date.slice(8)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Activity */}
        <div className="w-full md:w-[380px] flex flex-col bg-zinc-50 dark:bg-zinc-900/50 overflow-hidden shrink-0">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800"><h3 className="text-sm font-semibold">Activity</h3></div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {activityLog.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No activity yet</p>}
            {activityLog.map((entry, i) => (
              <div key={entry.id || i} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-xs font-medium shrink-0">{(entry.created_by_username || '?').charAt(0).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium">{entry.created_by_username}</span><span className="text-[10px] text-zinc-400">{new Date(entry.created_at).toLocaleString()}</span></div>
                  <div className="bg-white dark:bg-zinc-800 rounded-xl p-3 border border-zinc-200 dark:border-zinc-700"><p className="text-sm whitespace-pre-wrap">{entry.description}</p></div>
                  {entry.next_followup && <span className="text-[11px] text-amber-600 flex items-center gap-1 mt-1"><AlarmClock className="h-3 w-3" />{new Date(entry.next_followup).toLocaleString()}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 space-y-2">
            <Textarea placeholder="Write a note..." value={activityForm.description} onChange={e => setActivityForm(p => ({...p, description: e.target.value}))} className="min-h-[60px] text-sm"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addActivity(); } }} />
            <div className="flex items-center gap-2">
              <Input type="datetime-local" value={activityForm.next_followup} onChange={e => setActivityForm(p => ({...p, next_followup: e.target.value}))} className="flex-1 text-xs h-8" />
              <Button size="sm" onClick={addActivity}><Plus className="h-3 w-3 mr-1" /> Add</Button>
            </div>
          </div>
        </div>
      </div>
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} recordType="habit" recordId={habit.id} recordName={habit.title} />
    </div>
  );
}
